"use client";

import { motion } from "framer-motion";
import { fadeInUp, staggerContainer } from "@/lib/animations";
import { Award, Presentation, Briefcase, Users2 } from "lucide-react";

const offerings = [
  {
    icon: Presentation,
    title: "Exhibitions",
    description:
      "Showcase your work in our curated gallery spaces. Regular exhibitions featuring local and international designers, providing exposure and networking opportunities.",
    color: "text-blue-500 dark:text-blue-400",
    bgColor: "bg-blue-500/10",
    gradientFrom: "from-blue-500/20",
    gradientTo: "to-cyan-500/5",
    accentColor: "bg-blue-500 dark:bg-blue-400",
  },
  {
    icon: Award,
    title: "Competitions",
    description:
      "Participate in design challenges and competitions. Win recognition, prizes, and opportunities to collaborate with leading brands and organizations.",
    color: "text-primary",
    bgColor: "bg-primary/10",
    gradientFrom: "from-primary/20",
    gradientTo: "to-green-500/5",
    accentColor: "bg-primary",
  },
  {
    icon: Briefcase,
    title: "Workshops",
    description:
      "Learn from industry experts through hands-on workshops. Develop new skills in design, branding, UX/UI, and creative entrepreneurship.",
    color: "text-purple-500 dark:text-purple-400",
    bgColor: "bg-purple-500/10",
    gradientFrom: "from-purple-500/20",
    gradientTo: "to-pink-500/5",
    accentColor: "bg-purple-500 dark:bg-purple-400",
  },
  {
    icon: Users2,
    title: "Networking",
    description:
      "Connect with a vibrant community of creatives. Regular networking events, meetups, and collaborative sessions to grow your professional network.",
    color: "text-orange-500 dark:text-orange-400",
    bgColor: "bg-orange-500/10",
    gradientFrom: "from-orange-500/20",
    gradientTo: "to-red-500/5",
    accentColor: "bg-orange-500 dark:bg-orange-400",
  },
];

export default function WhatWeOffer() {
  return (
    <section
      id="offers"
      className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
    >
      {/* Background decorative elements */}
      <div className="absolute top-40 left-20 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-40 right-20 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />

      <div className="container mx-auto max-w-7xl relative z-10">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* Section Header */}
          <motion.div
            variants={fadeInUp}
            className="text-center mb-12 sm:mb-16 lg:mb-20"
          >
            <h2 className="text-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-4">
              What We Offer
            </h2>
            <p className="text-text text-base sm:text-lg md:text-xl max-w-3xl mx-auto px-4 leading-relaxed">
              Comprehensive services and opportunities designed to support and
              elevate your creative journey
            </p>
          </motion.div>

          {/* Offerings Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-10">
            {offerings.map((offering, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="group relative"
              >
                {/* Card */}
                <div className="relative h-full bg-card/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl border border-border shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                  {/* Gradient overlay on hover */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${offering.gradientFrom} ${offering.gradientTo} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                  />

                  {/* Top accent bar */}
                  <div
                    className={`absolute top-0 left-0 right-0 h-1.5 ${offering.accentColor} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}
                  />

                  {/* Content */}
                  <div className="relative z-10 p-6 sm:p-8 lg:p-10 flex flex-col h-full">
                    {/* Icon container with floating effect */}
                    <div className="mb-6 sm:mb-8">
                      <motion.div
                        whileHover={{ y: -8 }}
                        transition={{ type: "spring", stiffness: 300 }}
                        className={`inline-flex w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-2xl sm:rounded-3xl ${offering.bgColor} items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-500`}
                      >
                        <offering.icon
                          className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 ${offering.color} group-hover:scale-110 transition-transform duration-500`}
                        />
                      </motion.div>
                    </div>

                    {/* Title */}
                    <h3 className="text-heading text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-5 group-hover:text-primary transition-colors duration-300">
                      {offering.title}
                    </h3>

                    {/* Description */}
                    <p className="text-text text-sm sm:text-base lg:text-lg leading-relaxed flex-grow">
                      {offering.description}
                    </p>

                    {/* Decorative elements */}
                    <div className="absolute bottom-6 right-6 w-32 h-32 opacity-0 group-hover:opacity-10 transition-opacity duration-500">
                      <offering.icon className="w-full h-full text-heading" />
                    </div>

                    {/* Corner decoration */}
                    <div
                      className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${offering.gradientFrom} ${offering.gradientTo} opacity-0 group-hover:opacity-30 rounded-bl-full transition-opacity duration-500`}
                    />
                  </div>

                  {/* Bottom left accent */}
                  <div className="absolute bottom-0 left-0 w-24 h-24 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div
                      className={`absolute inset-0 ${offering.accentColor} opacity-20 rounded-tr-full`}
                    />
                  </div>

                  {/* Animated border effect */}
                  <div className="absolute inset-0 rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div
                      className={`absolute inset-0 rounded-2xl sm:rounded-3xl border-2 ${offering.accentColor} opacity-20`}
                    />
                  </div>
                </div>

                {/* Floating shadow effect */}
                <div
                  className={`absolute inset-0 rounded-2xl sm:rounded-3xl ${offering.accentColor} opacity-0 group-hover:opacity-20 blur-xl transform translate-y-4 transition-all duration-500 -z-10`}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
